/*
 *   File:           admin-constants.ts
 *   Description:    Collection of utility constants, helpers and misc
 *                   constants for storing static data for the Admin Module
 *   Author:         PreVeil, LLC
 */
import { DataExportRequestStatus } from "..";
import keyMirror from "keymirror";

export const AdminApprovalGroupRoles = ["admin_approval_group", "export_approval_group"];

export const readOnlyStates: Record<string, boolean> = {
  pending: true,
  admin: true,
  invited: true
};

export type AdminApprovalGroupRole = typeof AdminApprovalGroupRoleMapping[keyof typeof AdminApprovalGroupRoleMapping];

export const AdminApprovalGroupRoleMapping = {
  admin_approval_group: {
    name: "Admin Management",
    role: "admin_approval_group",
    all_admin: true,
    details: `
      <p><b>Admin management</b> is a trusted group to approve Organization level actions. These actions include setting user recovery group and assigning approval groups to activities.</p>
      <p><b>NOTE:</b> All approvers assigned to Admin Management must be administrators.</p>
    `
  },
  export_approval_group: {
    name: "Data Export",
    role: "export_approval_group",
    all_admin: false,
    details: `
      <p><b>Data Export</b> is a feature of PreVeil to export the data of your organization.</p>
      <p>Data Exports require approval to initiate, this assigns the group in charge of overseeing that export.</p>
    `
  }
};


// Users List headers for CSV File.
export const HEADERS: string[] = [
  "Display Name",
  "User Email",
  "Account Type",
  "Status",
  "Department",
  "Role",
  "Recovery Group",
];

// Device List headers for CSV File.
export const CERTIFICATE_HEADERS: string[] = [
  "User Id",
  "Device Name",
  "Device Id",
  "User Name",
  "Device Type",
  "Active",
  "Locked By",
  "Lock Time",
];

// Byte Order Mark (BOM)
// it may be required by some apps to be present in UTF-8 encoded files.
export const BOM = "\ufeff";

export const UserValidationState = keyMirror({
  duplicate: null,
  invalid: null,
  valid_subsume: null,
  valid_external: null
});

export const TopicMap = {
  All: {
    name: "",
    action_list: [],
    action_map: {},
  },
  Admin: {
    name: "admin",
    action_list: [
      "All",
      "Create Organization",
      "Users",
      "Approval Group",
      "Approval Request",
      "Whitelist",
    ],
    action_map: {
      All: [],
      "Create Organization": ["create_organization"],
      Users: ["invite_users_to_org", "delete_user", "update_account"],
      "Approval Group": ["create_approval_group", "apply_approval_group"],
      "Approval Request": [
        "create_approval_request",
        "approver_responded_to_request",
        "delete_approval_request",
      ],
      Whitelist: ["add_to_whitelist", "remove_from_whitelist", "enable_whitelist"],
    },
  },
  System: {
    name: "system",
    action_list: [
      "All",
      "Users",
      "Approval Group",
      "Approval Request",
      "Approval Recovery",
      "Device",
    ],
    action_map: {
      All: [],
      Users: ["claim_account", "invite_to_preveil", "invitee_joined_preveil"],
      "Approval Group": [
        "set_approval_group",
        "added_to_approval_group",
        "rekey_and_set_approval_group",
      ],
      "Approval Request": [
        "create_approval_request",
        "approval_requested",
        "approver_responded_to_request",
        "responded_to_request",
        "delete_approval_request",
        "delete_pending_approval",
      ],
      "Approval Recovery": [
        "begin_account_recovery",
        "rg_member_retrieved_shard",
        "retrieve_users_shard",
      ],
      Device: ["register_device", "lock_device", "unlock_device", "delete_device"],
    },
  },
  Email: {
    name: "email",
    action_list: [],
    action_map: {},
  },
  Drive: {
    name: "drive",
    action_list: ["All", "Edit", "Share"],
    action_map: {
      All: [],
      Edit: [
        "create_collection",
        "create_collection_node",
        "delete_collection_node",
        "update_collection",
        "rename_file",
        "move_file",
        "rename_directory",
        "move_directory",
      ],
      Share: ["share_collection", "respond_to_share_invitation", "share_response"],
    },
  },
  Group: {
    name: "group",
    action_list: [],
    action_map: {},
  },
};

export const OrganizationLevelSettings = keyMirror({
  storage_backend: null,
  require_biometrics: null,
  disable_file_export: null,
  present_cui_notice: null,
  sync_collections_by_default: null,
});

export const DeviceMessageTooltip = {
  lock: "Lock device and remove local drive data",
  unlock: "Unlock device",
  delete: "Delete device and remove local drive data"
};

export const AddUsersMessaging = {
  default: "The email address already exists.",
  pending: "This user exists and is pending acceptance.",
  invalid_email_message: "The email address is not in the correct format.",
};

export const CSVStatusHeading = {
  duplicate: "Accounts already in Organization.",
  invalid: "Accounts with error.",
  valid_subsume: "Existing PreVeil Accounts.",
  valid_external: "Accounts to be created."
};

export const CSV_COLUMNS = {
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email Address",
  department: "Department"
};

export const dropdownLabels = {
  users: "All Users",
  content: {
    email: "Email",
    mail: "Email",
    drive: "Drive",
    logs: "Logs",
    acl_report: "ACL Report",
    org_acl_report: "ACL Report"
  },
  date: "Forever"
};

export const buttonState = {
  complete: DataExportRequestStatus.complete,
  started: DataExportRequestStatus.started,
  running: DataExportRequestStatus.running
};

export const contentLabels = {
  users: "All Users",
  content: {
    emails: "Email",
    email: "Email",
    mail: "Email",
    files: "Drive",
    drive: "Drive",
    logs: "Logs",
    acl_report: "ACL Report",
    org_acl_report: "ACL Report"
  },
  date: "Forever"
};

export const pending_account_updates_types: Array<{ key: string, message: string }> = [
  { key: "user_pending_group_change_total", message: "Recovery group approval pending." },
  { key: "user_pending_role_change_total", message: "Admin approval group pending." },
  { key: "user_pending_delete_total", message: "User account delete pending." },
];

export const tab_event_key = {
  enter_users: "Create User Group",
  csv_upload: "Importing User Group Emails",
};